export type PlanKey = "growth" | "professional" | "enterprise";
export type BillingInterval = "monthly" | "quarterly" | "yearly";

export type PricingCatalog = {
  billingMode?: string;
  launchTrialDays?: number;
  launchTrialLabel?: string;
  display?: {
    visiblePlans: PlanKey[];
    visibleIntervals: BillingInterval[];
  };
  discounts: {
    quarterlyPercent: number;
    yearlyPercent: number;
  };
  plans: {
    growth: {
      name?: string;
      description?: string;
      ctaLabel?: string;
      footerLine?: string;
      features: string[];
    };
    professional: {
      name?: string;
      description?: string;
      ctaLabel?: string;
      footerLine?: string;
      features: string[];
    };
    enterprise: {
      name?: string;
      features: string[];
    };
  };
  computed: {
    growth: Record<
      BillingInterval,
      { displayMonthly: number; billedLine: string; oldPriceMonthly: number }
    >;
    professional: Record<
      BillingInterval,
      { displayMonthly: number; billedLine: string; oldPriceMonthly: number }
    >;
  };
};

const LAUNCH_FREE_DISPLAY = {
  visiblePlans: ["growth", "professional"] as PlanKey[],
  visibleIntervals: ["monthly"] as BillingInterval[],
};

const PAID_DISPLAY = {
  visiblePlans: ["growth", "professional", "enterprise"] as PlanKey[],
  visibleIntervals: ["monthly", "quarterly", "yearly"] as BillingInterval[],
};

const SAFEPAY_DISPLAY = {
  visiblePlans: ["growth", "professional", "enterprise"] as PlanKey[],
  visibleIntervals: ["monthly"] as BillingInterval[],
};

/** Keep in sync with backend pricingDisplayCatalog professional plan prices. */
const PROFESSIONAL_MONTHLY_PRICE = 50;
const PROFESSIONAL_INCLUDED_SEATS = 5;
const PROFESSIONAL_EXTRA_SEAT_PRICE = 10;
const GROWTH_MONTHLY_PRICE = 20;

const professionalSeatFeature = `${PROFESSIONAL_INCLUDED_SEATS} accounts included in $${PROFESSIONAL_MONTHLY_PRICE} additional accounts will be billed $${PROFESSIONAL_EXTRA_SEAT_PRICE} per account`;

export const FALLBACK_PRICING_CATALOG: PricingCatalog = {
  billingMode: "safepay",
  launchTrialDays: 7,
  launchTrialLabel: "7-day free trial",
  display: SAFEPAY_DISPLAY,
  discounts: {
    quarterlyPercent: 15,
    yearlyPercent: 25,
  },
  plans: {
    growth: {
      name: "Solo",
      description: "Everything you need to run high performing outbound campaigns.",
      ctaLabel: "Start 7 Day Free Trial",
      footerLine: "One LinkedIn sender account.",
      features: [
        "1 LinkedIn sender account",
        "Unified inbox for conversations",
        "Unlimited imports",
        "All LinkedIn actions supported",
        "Reply detection",
        "Unlimited message templates",
        "Unlimited campaigns",
        "Unlimited AI personalization",
        "Campaign analytics",
        "Activity control center",
        "Free secure proxy",
        "Chat support",
      ],
    },
    professional: {
      name: "Professional",
      description: "Built for teams and agencies scaling outbound.",
      ctaLabel: "Start 7 Day Free Trial",
      features: [
        "Everything in Solo",
        professionalSeatFeature,
        "Priority onboarding call",
        "Dedicated account manager",
        "Advanced campaign analytics",
        "Premium support",
        "Team reporting dashboard",
        "Per-sender daily limit configuration",
      ],
    },
    enterprise: {
      name: "Enterprise",
      features: [
        "Everything in Professional",
        "Unlimited sender accounts & users",
        "White-label reporting",
        "API access & custom integrations",
        "Dedicated infrastructure & IPs",
        "Client workspace separation",
        "SLA support",
      ],
    },
  },
  computed: {
    growth: {
      monthly: {
        displayMonthly: GROWTH_MONTHLY_PRICE,
        billedLine: "",
        oldPriceMonthly: GROWTH_MONTHLY_PRICE,
      },
      quarterly: {
        displayMonthly: GROWTH_MONTHLY_PRICE,
        billedLine: "",
        oldPriceMonthly: GROWTH_MONTHLY_PRICE,
      },
      yearly: {
        displayMonthly: GROWTH_MONTHLY_PRICE,
        billedLine: "",
        oldPriceMonthly: GROWTH_MONTHLY_PRICE,
      },
    },
    professional: {
      monthly: {
        displayMonthly: PROFESSIONAL_MONTHLY_PRICE,
        billedLine: "",
        oldPriceMonthly: PROFESSIONAL_MONTHLY_PRICE,
      },
      quarterly: {
        displayMonthly: PROFESSIONAL_MONTHLY_PRICE,
        billedLine: "",
        oldPriceMonthly: PROFESSIONAL_MONTHLY_PRICE,
      },
      yearly: {
        displayMonthly: PROFESSIONAL_MONTHLY_PRICE,
        billedLine: "",
        oldPriceMonthly: PROFESSIONAL_MONTHLY_PRICE,
      },
    },
  },
};

export function normalizePricingCatalog(data: unknown): PricingCatalog | null {
  if (!data || typeof data !== "object") return null;
  const payload = data as Partial<PricingCatalog>;
  if (!payload.plans?.growth || !payload.computed?.growth) return null;
  const launchFree = payload.billingMode === "launch_free";
  return {
    ...FALLBACK_PRICING_CATALOG,
    ...payload,
    plans: {
      ...FALLBACK_PRICING_CATALOG.plans,
      ...payload.plans,
      growth: { ...FALLBACK_PRICING_CATALOG.plans.growth, ...payload.plans.growth },
      professional: {
        ...FALLBACK_PRICING_CATALOG.plans.professional,
        ...payload.plans.professional,
      },
      enterprise: {
        ...FALLBACK_PRICING_CATALOG.plans.enterprise,
        ...payload.plans.enterprise,
      },
    },
    computed: {
      growth: {
        ...FALLBACK_PRICING_CATALOG.computed.growth,
        ...payload.computed.growth,
      },
      professional: {
        ...FALLBACK_PRICING_CATALOG.computed.professional,
        ...payload.computed.professional,
      },
    },
    display:
      payload.display ||
      (launchFree
        ? LAUNCH_FREE_DISPLAY
        : String(payload.billingMode || "").toLowerCase() === "safepay"
          ? SAFEPAY_DISPLAY
          : PAID_DISPLAY),
  };
}

export async function fetchPricingCatalog(paymentUrl: string): Promise<PricingCatalog> {
  try {
    const response = await fetch(`${paymentUrl.replace(/\/$/, "")}/pricingCatalog`);
    if (!response.ok) return FALLBACK_PRICING_CATALOG;
    const data = await response.json();
    return normalizePricingCatalog(data) || FALLBACK_PRICING_CATALOG;
  } catch {
    return FALLBACK_PRICING_CATALOG;
  }
}

export function isLaunchFreeBillingCatalog(catalog: PricingCatalog): boolean {
  return catalog.billingMode === "launch_free";
}

export function getVisiblePlans(catalog: PricingCatalog): Array<"growth" | "professional"> {
  const visible = catalog.display?.visiblePlans || [];
  return visible.filter(
    (plan): plan is "growth" | "professional" => plan === "growth" || plan === "professional"
  );
}

export function getVisibleIntervals(catalog: PricingCatalog): BillingInterval[] {
  // Safepay currently supports monthly billing only.
  if (String(catalog.billingMode || "").toLowerCase() === "safepay") {
    return ["monthly"];
  }
  if (catalog.display?.visibleIntervals?.length) {
    return catalog.display.visibleIntervals;
  }
  return isLaunchFreeBillingCatalog(catalog)
    ? ["monthly"]
    : ["monthly", "quarterly", "yearly"];
}

export function shouldShowEnterprisePlan(catalog: PricingCatalog): boolean {
  return (catalog.display?.visiblePlans || []).includes("enterprise");
}

export function getGrowthPlanName(catalog: PricingCatalog): string {
  return catalog.plans.growth.name || "Solo";
}

export function getProfessionalPlanName(catalog: PricingCatalog): string {
  return catalog.plans.professional.name || "Professional";
}
